import React, { useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    useDeleteAssetImageMutation,
    useDeleteAssetMutation,
    useGetAssetDetailsQuery,
    useReplaceFeaturedImageMutation,
    useUpdateAssetDetailsMutation,
    useUploadAssetImagesMutation,
} from '../../api/asset/assetApiSlice';
import { bookingApiSlice } from '../../api/booking/bookingApiSlice';
import {
    AccommodationDetail,
    AccommodationDetailAmenity,
    AccommodationDetailPolicy,
    AssetDetailsResponse,
    decimalToNumber,
} from '../../api/asset/assetApiTypes';
import { formatCurrency } from '../../utils';
import { getCurrentLang } from '../../i18n/i18n';
import { makeHasPermissionSelector, tenantSelector } from '../../selector/authenticationSelector';
import { useIsMaster } from '../../common/useIsMaster';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import CustomButton from '../../components/CustomButton';
import HardDeleteDialog from '../common/HardDeleteDialog';
import ObjectLifecycleDialog, { ObjectLifecycleAction } from './components/ObjectLifecycleDialog';
import Permission from '../../common/permissions';
import { makeProductDetailsUrl, paths } from '../../routes';
import BasicInfoSection from './components/BasicInfoSection';
import DetailField from './components/DetailField';
import LocationSection, { extractFloorFromName } from './components/LocationSection';
import AccommodationSection from './components/AccommodationSection';
import ImagesSection from './components/ImagesSection';
import { objectCover, objectProductFields } from './objectCover';
import PoliciesSection from './components/PoliciesSection';
import AdditionalServicesSection from './components/AdditionalServicesSection';
import PricingSection from './components/PricingSection';
import RecentBookingsSection from './components/RecentBookingsSection';
import OpenIssueReportsSection from './components/OpenIssueReportsSection';
import OpeningHoursSection from './components/OpeningHoursSection';
import RoomHouseSection from './components/RoomHouseSection';
import RoomAccessSection from './components/RoomAccessSection';
import RoomAllocationWindows from '../organizations/allocationWindows/RoomAllocationWindows';
import AccessCodeSection from './components/AccessCodeSection';
import { isSelfServiceTenant } from '../../common/tenantStore';
import ObjectIdentitySection from './components/ObjectIdentitySection';
import {
    useGetGroupAccessQuery,
    useGetProductsQuery,
    useUpdateGroupProductMutation,
} from '../../api/group/groupAdminApiSlice';
import ProductAssignmentSection from './components/ProductAssignmentSection';
import { useGetOrganizationsQuery } from '../../api/organization/organizationApiSlice';
import {
    invalidateInventoryReaders,
    useUpdateObjectMutation,
} from '../../api/inventory/inventoryApiSlice';
import { readInventoryFailure } from '../../api/inventory/inventoryErrors';
import { OpeningHourEntry } from '../../types/locations';
import { zoneOfLocation } from '../../utils/stationTime';
import { isRoomFamily, spaceOf } from '../../utils/useCases';
import { useDefaultTaxCategoryId } from '../../components/tax/useDefaultTaxCategoryId';

export interface FormService {
    id: string;
    serviceName: string;
    langNames: Record<string, string>;
    unitPrice: number;
    isPaymentCompulsory: boolean;
    quantity: boolean;
    /** #1353 VAT category id; '' = unassigned. */
    taxCategoryId: string;
    /** #2873: per_booking | per_hour | per_day; null = per piece (unit price
     *  × chosen quantity). per_person is not offered — the backend rejects it. */
    priceUnit: string | null;
    isNew?: boolean;
}

/** Per-unit facts, written through the inventory PATCH (merged, not replaced). */
export interface ObjectAttributesForm {
    serialNumber: string;
    volumeCm3: string;
    note: string;
}

export interface AssetFormData {
    name: string;
    floor: string;
    objectAttributes: ObjectAttributesForm;
    accomodationdetail: AccommodationDetail;
    additionalServices: FormService[];
    openingHours: OpeningHourEntry[];
}

/** Does the open edit hold anything a save would write? */
function editHasChanges(edit: {
    mode: 'view' | 'edit';
    data: AssetDetailsResponse | undefined;
    formData: AssetFormData | null;
    selectedProductId: string | null;
    pendingFeaturedImage: unknown;
    pendingAdditionalImages: unknown[];
    pendingDeletions: unknown[];
}): boolean {
    if (edit.mode !== 'edit') return false;
    if (edit.pendingFeaturedImage || edit.pendingAdditionalImages.length > 0
        || edit.pendingDeletions.length > 0 || edit.selectedProductId !== null) return true;
    return Boolean(edit.data && edit.formData
        && JSON.stringify(edit.formData) !== JSON.stringify(buildInitialFormData(edit.data)));
}

function buildInitialFormData(data: AssetDetailsResponse): AssetFormData {
    const detail = data.accomodationdetail ?? {};
    return {
        name: data.name,
        // Prefer the dedicated floor column; fall back to the legacy
        // floor-in-name convention for assets created before it was first-class.
        floor: data.floor ?? extractFloorFromName(data.name) ?? '',
        objectAttributes: {
            serialNumber: typeof detail.serialNumber === 'string' ? detail.serialNumber : '',
            volumeCm3: detail.volumeCm3 != null ? String(detail.volumeCm3) : '',
            note: typeof detail.note === 'string' ? detail.note : '',
        },
        accomodationdetail: {
            // Spread first so keys this form does not model survive the round
            // trip. `PUT /admin/asset/:assetId` REPLACES `accomodationdetail`
            // wholesale (assetController.ts:748), and per-unit facts written by
            // `PATCH /admin/inventory/objects/:assetId` — serialNumber,
            // volumeCm3, note — live in the same JSON. Rebuilding from a fixed
            // key list silently dropped them on the next name edit.
            ...detail,
            guests: detail.guests,
            beds: detail.beds,
            baths: detail.baths,
            rooms: detail.rooms,
            kitchen: detail.kitchen,
            bedrooms: detail.bedrooms,
            outdoor: detail.outdoor ?? '',
            amenities: detail.amenities ? detail.amenities.map((a) => ({ ...a })) : [],
            facilityDescription: detail.facilityDescription ?? '',
            facilityDescriptions: { de: '', en: '', ...detail.facilityDescriptions },
            importantNote: detail.importantNote ?? '',
            importantNotes: { de: '', en: '', ...detail.importantNotes },
            policies: detail.policies ? detail.policies.map((p) => ({
                ...p,
                titles: { de: '', en: '', ...p.titles, ...(p.title && !p.titles ? { de: p.title, en: p.title } : {}) },
                descriptions: { de: '', en: '', ...p.descriptions, ...(p.description && !p.descriptions ? { de: p.description, en: p.description } : {}) },
            })) : [],
            floorNumber: { de: '', en: '', ...detail.floorNumber },
            floorName: detail.floorName ? { ...detail.floorName } : undefined,
            floorDirection: detail.floorDirection ? { ...detail.floorDirection } : undefined,
            apartmentType: detail.apartmentType ? { ...detail.apartmentType } : undefined,
        },
        additionalServices: data.assetAdditionalServices.map((s) => ({
            id: s.id,
            serviceName: s.serviceName,
            langNames: { de: '', en: '', ...s.langNames },
            unitPrice: decimalToNumber(s.unitPrice) ?? 0,
            isPaymentCompulsory: s.isPaymentCompulsory,
            quantity: s.quantity ?? false,
            taxCategoryId: s.taxCategoryId ?? '',
            priceUnit: s.priceUnit ?? null,
        })),
        openingHours: (data.Group?.location?.openingHours ?? []).map((h) => ({
            weekday: h.weekday,
            times: { fromHour: h.times.fromHour, toHour: h.times.toHour },
        })),
    };
}


/** The assignment to run on save, or null when nothing changes. */
const pendingProductChange = (
    editScope: 'full' | 'object',
    groupId: string | undefined,
    productInfo: { id: string } | null,
    selectedProductId: string | null,
): { groupId: string, productId: string } | null => (
    editScope === 'full'
    && groupId
    && productInfo
    && selectedProductId
    && selectedProductId !== productInfo.id
        ? { groupId, productId: selectedProductId }
        : null
);

/** The detail response's productInfo, normalized to null. */
const detailProductInfo = (
    data: { productInfo?: AssetDetailsResponse['productInfo'] } | undefined,
): NonNullable<AssetDetailsResponse['productInfo']> | null => data?.productInfo ?? null;

/**
 * The product's photo in the object page's Product box, as a small square
 * (the end-user surfaces show it square), labelled as the product's.
 */
const ProductPhotoField: React.FC<{ photo: string | null | undefined, label: string }> = ({ photo, label }) => (
    <DetailField label={label} value={photo ?? undefined} emptyLabel={'na'}>
        {photo ? <img src={photo} alt={label} className={'w-16 h-16 rounded-lg object-cover'} /> : undefined}
    </DetailField>
);

const AssetDetailPage: React.FC = () => {
    const { assetId } = useParams<{ assetId: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [mode, setMode] = useState<'view' | 'edit'>('view');
    const [formData, setFormData] = useState<AssetFormData | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    // Pending product assignment (pricing stream slice 1); null = unchanged.
    const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
    const [pendingFeaturedImage, setPendingFeaturedImage] = useState<File | null>(null);
    const [pendingAdditionalImages, setPendingAdditionalImages] = useState<File[]>([]);
    const [pendingDeletions, setPendingDeletions] = useState<string[]>([]);
    const [openingHoursValid, setOpeningHoursValid] = useState(true);

    const { data, isLoading, isError } = useGetAssetDetailsQuery(
        { assetId: assetId ?? '' },
        { skip: !assetId },
    );
    // Member room access (#1926). Skipped until the group id is known — a
    // brand-new object has none until it is saved, which is also why this
    // configuration lives on the detail page and not in the create flow.
    const groupId = data?.Group?.id;
    // Read-only here since option C: rooms are assigned on the organization,
    // through another API slice, so this refetches on mount instead of relying
    // on a tag the other slice cannot invalidate.
    const { data: groupAccess } = useGetGroupAccessQuery(
        { groupId: groupId ?? '' },
        { skip: !groupId, refetchOnMountOrArgChange: true },
    );
    const { data: organizationData } = useGetOrganizationsQuery({ pageSize: 200 });
    const organizations = organizationData?.items ?? [];

    // Pricing stream slice 1: the object's Product and the assignment control.
    // Products are fetched only where the section renders (room scope with a
    // resolved productInfo).
    const productInfo = detailProductInfo(data);
    const { data: allProducts = [] } = useGetProductsQuery(undefined, {
        skip: !productInfo,
    });
    const [updateGroupProduct] = useUpdateGroupProductMutation();

    const [updateAsset, { isLoading: isSaving }] = useUpdateAssetDetailsMutation();
    const [updateObject, { isLoading: isSavingObject }] = useUpdateObjectMutation();
    const [deleteAssetMutation] = useDeleteAssetMutation();
    const [uploadImages, { isLoading: isUploadingImages }] = useUploadAssetImagesMutation();
    const [replaceFeaturedImage, { isLoading: isReplacingFeaturedImage }] = useReplaceFeaturedImageMutation();
    const [deleteImage] = useDeleteAssetImageMutation();
    const dispatch = useDispatch();

    // The hard delete is master-only (#2636), the same gate as deleting a
    // booking or a code — not `delete:assets`, which every owner holds and
    // which retiring the object already covers.
    const isMaster = useIsMaster();
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    // "Ausmustern" / "Wieder in Betrieb nehmen" (PO, 2026-10-15): the owner's
    // reversible way to take an object out — `delete:assets`, the grant the
    // retire endpoint checks.
    const canRetire = useSelector(makeHasPermissionSelector(Permission.deleteAssets()));
    // Saving writes through routes that check `create:assets` (the asset, its
    // images, the room's product, the object attributes), so Edit is offered
    // only with it. The page itself stays reachable read-only.
    const canEdit = useSelector(makeHasPermissionSelector(Permission.createAssets()));
    const [lifecycleAction, setLifecycleAction] = useState<ObjectLifecycleAction | null>(null);

    // Static access codes are a self-service-only surface: legacy per-tenant
    // DBs know neither the `static_code` mechanism nor the code column, so the
    // endpoint answers 409 by design. Not offering the section beats rendering
    // a failure a legacy tenant can never resolve.
    const selectedTenant = useSelector(tenantSelector);
    const showAccessCode = isSelfServiceTenant(selectedTenant);


    const handleDeleted = useCallback(() => {
        dispatch(bookingApiSlice.util.invalidateTags(['assetAvailabilityData']));
        navigate(paths.assets);
    }, [dispatch, navigate]);

    const handleEdit = useCallback(() => {
        if (!data) return;
        setFormData(buildInitialFormData(data));
        setSaveError(null);
        setPendingFeaturedImage(null);
        setPendingAdditionalImages([]);
        setPendingDeletions([]);
        setOpeningHoursValid(true);
        setSelectedProductId(null);
        setMode('edit');
    }, [data]);

    const handleCancel = useCallback(() => {
        setFormData(null);
        setSaveError(null);
        setPendingFeaturedImage(null);
        setPendingAdditionalImages([]);
        setPendingDeletions([]);
        setOpeningHoursValid(true);
        setSelectedProductId(null);
        setMode('view');
    }, []);

    // Anything typed in this edit that a save would write. "Dem Haus zuordnen"
    // waits for it: assigning moves the room, and edits made against the old
    // placement would then be saved over the new one (PO, 2026-10-08).
    const hasUnsavedChanges = editHasChanges({
        mode, data, formData, selectedProductId,
        pendingFeaturedImage, pendingAdditionalImages, pendingDeletions,
    });

    const useCases = data?.Group?.compartmentTypes ?? [];
    // Opening hours apply to the rooms booked by the hour (venues), not to
    // night-booked ones — see the same rule on AssetCreatePage.
    const showsOpeningHours = useCases.some((uc) => spaceOf(uc)?.timeUnit === 'hour');
    // A1 a (plan room-houses-and-combining-rooms): the hours are the
    // LOCATION's. When other rooms share it (a house), editing them here would
    // change them for every room, so they are read-only and never sent.
    const hoursEditable = showsOpeningHours && data?.openingHoursShared !== true;

    // What this page's Edit covers.
    //
    // A room (the room family, `isRoomFamily`) is a single listing whose product and object
    // are the same thing in practice, so its edit stays exactly as it was —
    // full, including price and images. Everything else is one unit out of a
    // fleet: its product facts are shared with every sibling, so editing them
    // here would silently reprice the whole product. Those become read-only
    // and link to the product page; the object keeps what is true of it alone.
    //
    // This is also the gap the page had: non-room objects previously had no
    // Edit affordance at all.
    const isRoomObject = useCases.some((uc) => isRoomFamily(uc));
    const editScope: 'full' | 'object' = isRoomObject ? 'full' : 'object';
    // What a ROOM object owns itself — its photos and its long-stay
    // discounts — is editable in room scope, one object or many under its
    // product; its prices never are (they are the product's, #2923).
    const objectMode: 'view' | 'edit' = editScope === 'full' ? mode : 'view';
    // The photo section's cover and its missing marker, by the server's
    // display rule (PO, 2026-11-03): a room's own photo, never a station
    // unit's product photo (that one shows in the Product box).
    const { cover, coverMissing } = objectCover(data, isRoomObject);
    const { productName, productDescription } = objectProductFields(data);

    const handleSave = useCallback(async () => {
        if (!formData || !assetId) return;
        setSaveError(null);

        // #1749: the name is required on create but was not enforced here, so an
        // object could be saved with it blanked. It is what every downstream
        // surface renders — the bookings list `asset_name`, the `%APARTMENTNAME%`
        // placeholder in customer emails — so a blank one produces a record that
        // looks saved and shows as nothing. Enforcing it also lets the marker be
        // unconditional instead of create-only.
        if (!formData.name?.trim()) {
            setSaveError(t('asset_detail.validation.name_required'));
            return;
        }

        // Prices are the PRODUCT's and are set in Services → Products, never
        // on an object — not even one that has its product to itself (PO,
        // 2026-09-28; #2923). So this page sends none and validates none.

        if (hoursEditable && !openingHoursValid) {
            setSaveError(t('asset_create.validation.opening_hours_invalid'));
            return;
        }

        try {
            // Save base prices first. updateAsset below invalidates the
            // assetDetails tag and triggers a refetch — we want that refetch
            // to observe the already-updated price scales.
            // `pricecategory` is shared by every object of the product, so a
            // write from one object's page would reprice its siblings. Only
            // the room scope — where product and object are the same listing —
            // may touch it.
            // Product assignment first: it changes which price list is live,
            // so the refetch after updateAsset must observe the new topology.
            const productChange = pendingProductChange(
                editScope, groupId, productInfo, selectedProductId,
            );
            if (productChange) {
                await updateGroupProduct(productChange).unwrap();
            }

            // Additive image operations first — if any of these fail, the old
            // images are still intact and the user can retry cleanly.
            if (pendingAdditionalImages.length > 0) {
                await uploadImages({ assetId, files: pendingAdditionalImages }).unwrap();
            }
            if (pendingFeaturedImage) {
                await replaceFeaturedImage({ assetId, file: pendingFeaturedImage }).unwrap();
            }

            await updateAsset({
                assetId,
                body: {
                    name: formData.name,
                    floor: formData.floor.trim() || null,
                    accomodationdetail: formData.accomodationdetail,
                    assetAdditionalServices: formData.additionalServices.map((s) => ({
                        ...(!s.isNew && { id: s.id }),
                        serviceName: s.langNames?.de || s.serviceName,
                        langNames: s.langNames,
                        unitPrice: s.unitPrice,
                        isPaymentCompulsory: s.isPaymentCompulsory,
                        quantity: s.quantity,
                        taxCategoryId: s.taxCategoryId,
                        priceUnit: s.priceUnit,
                    })),
                    // Option b (PO 2026-09-28): the long-stay discounts are the
                    // PRODUCT's — maintained in the Services price dialog and
                    // written to every object there. This page shows them
                    // read-only and sends none.
                    ...(hoursEditable ? { openingHours: formData.openingHours } : {}),
                },
            }).unwrap();

            // Per-unit facts LAST of the data writes, and deliberately after
            // `updateAsset`: the asset PUT replaces `accomodationdetail`
            // wholesale, while this PATCH merges into it. Running it second
            // means the merge lands on what the PUT just wrote, instead of the
            // PUT overwriting a freshly-patched serial.
            const attrs = formData.objectAttributes;
            const parsedVolume = attrs.volumeCm3.trim()
                ? Number.parseInt(attrs.volumeCm3.trim(), 10)
                : undefined;
            // Emptying the volume field is deliberately NOT treated as a
            // change: the backend clears a string key on `''`, but a numeric
            // one has no clear — null/undefined are skipped and `''` fails
            // validation. Firing a PATCH for it would do nothing while looking
            // like it saved. Clearing a stored volume needs a contract change;
            // logged on the collector.
            const volumeChanged = parsedVolume !== undefined
                && parsedVolume !== data?.accomodationdetail?.volumeCm3;
            const attributesChanged =
                attrs.serialNumber.trim() !== (data?.accomodationdetail?.serialNumber ?? '')
                || attrs.note.trim() !== (data?.accomodationdetail?.note ?? '')
                || volumeChanged;
            if (attributesChanged) {
                await updateObject({
                    assetId,
                    attributes: {
                        serialNumber: attrs.serialNumber.trim(),
                        note: attrs.note.trim(),
                        ...(parsedVolume !== undefined && !Number.isNaN(parsedVolume)
                            ? { volumeCm3: parsedVolume }
                            : {}),
                    },
                }).unwrap();
                invalidateInventoryReaders(dispatch);
            }

            // Destructive image deletions last — deferred so a mid-flow
            // failure above leaves the existing images untouched.
            if (pendingDeletions.length > 0) {
                await Promise.all(
                    pendingDeletions.map((imageUrl) => deleteImage({ assetId, imageUrl }).unwrap()),
                );
            }

            dispatch(bookingApiSlice.util.invalidateTags(['assetAvailabilityData']));

            setPendingFeaturedImage(null);
            setPendingAdditionalImages([]);
            setPendingDeletions([]);
            setFormData(null);
            setMode('view');
        } catch (err: unknown) {
            const failure = readInventoryFailure(err);
            // The inventory PATCH reports every bad attribute at once; keep
            // that rather than collapsing it to the first line.
            if (failure.fieldErrors.length > 0) {
                setSaveError(
                    failure.fieldErrors.map((e) => `${e.field}: ${e.message}`).join(' · '),
                );
                return;
            }
            setSaveError(failure.message ?? t('asset_detail.save_error') ?? 'Unknown error');
        }
    }, [formData, assetId,
        updateAsset, updateObject, uploadImages, replaceFeaturedImage, deleteImage,
        pendingFeaturedImage, pendingAdditionalImages, pendingDeletions, data, t, dispatch,
        hoursEditable, openingHoursValid, editScope,
        productInfo, selectedProductId, updateGroupProduct]);

    const updateFormField = useCallback(<K extends keyof AssetFormData>(key: K, value: AssetFormData[K]) => {
        setFormData((prev) => prev ? { ...prev, [key]: value } : prev);
    }, []);

    const updateAccommodation = useCallback((updates: Partial<AccommodationDetail>) => {
        setFormData((prev) => {
            if (!prev) return prev;
            return { ...prev, accomodationdetail: { ...prev.accomodationdetail, ...updates } };
        });
    }, []);

    const updateAmenities = useCallback((amenities: AccommodationDetailAmenity[]) => {
        updateAccommodation({ amenities });
    }, [updateAccommodation]);

    const updatePolicies = useCallback((policies: AccommodationDetailPolicy[]) => {
        updateAccommodation({ policies });
    }, [updateAccommodation]);

    const updateService = useCallback((serviceId: string, updates: Partial<FormService>) => {
        setFormData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                additionalServices: prev.additionalServices.map((s) =>
                    s.id === serviceId ? { ...s, ...updates } : s,
                ),
            };
        });
    }, []);

    const defaultTaxCategoryId = useDefaultTaxCategoryId();
    const addService = useCallback(() => {
        setFormData((prev) => {
            if (!prev) return prev;
            const newSvc: FormService = {
                id: `new-${Date.now()}`,
                serviceName: '',
                langNames: { de: '', en: '' },
                unitPrice: 0,
                isPaymentCompulsory: false,
                quantity: false,
                // #1353/#2959: a new service starts on the tenant's default
                // category, shown in its picker so the operator sees and can
                // change it. Pricing never falls back to it: '' (no default, or
                // the catalogue not loaded yet) still withholds the breakdown and
                // says so, which is the prompt to choose.
                taxCategoryId: defaultTaxCategoryId,
                priceUnit: null,
                isNew: true,
            };
            return { ...prev, additionalServices: [...prev.additionalServices, newSvc] };
        });
    }, [defaultTaxCategoryId]);

    const removeService = useCallback((serviceId: string) => {
        setFormData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                additionalServices: prev.additionalServices.filter((s) => s.id !== serviceId),
            };
        });
    }, []);

    const updateOpeningHours = useCallback((hours: OpeningHourEntry[]) => {
        setFormData((prev) => prev ? { ...prev, openingHours: hours } : prev);
    }, []);

    const updateObjectAttributes = useCallback((updates: Partial<ObjectAttributesForm>) => {
        setFormData((prev) => prev
            ? { ...prev, objectAttributes: { ...prev.objectAttributes, ...updates } }
            : prev);
    }, []);

    const updateFloor = useCallback((floor: string) => {
        setFormData((prev) => prev ? { ...prev, floor } : prev);
    }, []);

    if (isLoading) {
        return (
            <div className={'p-6 flex items-center justify-center min-h-[400px]'}>
                <div className={'animate-spin rounded-full h-12 w-12 border-b-2 border-primary'} />
            </div>
        );
    }

    if (isError || !data) {
        return (
            <div className={'p-6 flex flex-col gap-4'}>
                <PageHeader>
                    <PageHeadline>{t('asset_detail.not_found.title')}</PageHeadline>
                </PageHeader>
                <div className={'bg-text-contrast rounded-[5px] shadow-sm p-6 flex flex-col items-center gap-4'}>
                    <p className={'text-foreground/60'}>{t('asset_detail.not_found.message')}</p>
                    <button
                        onClick={() => navigate(paths.assets)}
                        className={'text-primary hover:underline'}
                    >
                        {t('asset_detail.not_found.back_to_overview')}
                    </button>
                </div>
            </div>
        );
    }

    const statusColors: Record<string, string> = {
        available: 'bg-green-100 text-green-800',
        inUse: 'bg-blue-100 text-blue-800',
        blocked: 'bg-red-100 text-red-800',
    };

    return (
        <div className={'p-6 flex flex-col gap-6'}>
            {/* Page Header — sticky so the object name stays visible while
                the user scrolls through a long edit form */}
            <div className={'sticky top-16 z-30 bg-background -mx-6 px-6 -mt-6 pt-6 pb-3'}>
                <PageHeader>
                    <div className={'flex-grow'}>
                        <PageHeadline>{t('asset_detail.title')}</PageHeadline>
                        <p className={'text-sm text-foreground/60'}>
                            {mode === 'edit' ? formData?.name : data.name}
                        </p>
                    </div>
                    {mode === 'view' && (
                        <div className={'flex items-center gap-2'}>
                            {canRetire && (
                                <CustomButton
                                    title={t(data.archivedAt
                                        ? 'asset_detail.actions.reactivate'
                                        : 'asset_detail.actions.retire')}
                                    onClick={() => setLifecycleAction(data.archivedAt ? 'reactivate' : 'retire')}
                                    isFilled={false}
                                />
                            )}
                            {isMaster && (
                                <CustomButton
                                    title={t('asset_detail.actions.delete')}
                                    onClick={() => setIsDeleteOpen(true)}
                                    isFilled={false}
                                    variant={'delete'}
                                />
                            )}
                            {/* Offered for every object now. A room edits its
                                whole listing; anything else edits only what is
                                true of the one unit — see `editScope`. */}
                            {canEdit && (
                                <CustomButton
                                    title={t('asset_detail.actions.edit')}
                                    onClick={handleEdit}
                                    isFilled={true}
                                />
                            )}
                        </div>
                    )}
                </PageHeader>
            </div>

            {/* Save error */}
            {saveError && (
                <div className={'bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800'}>
                    {t('asset_detail.save_error')}: {saveError}
                </div>
            )}

            {/* Status */}
            <div className={'flex items-center gap-2 -mt-4'}>
                <span className={'text-sm text-foreground/60 font-medium'}>
                    {t('asset_detail.current_status')}
                </span>
                <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        statusColors[data.state] ?? 'bg-gray-100 text-gray-800'
                    }`}
                >
                    {t(`asset_detail.status.${data.state}`)}
                </span>
                {data.archivedAt && (
                    <span className={'px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'}>
                        {t('asset_detail.status.archived')}
                    </span>
                )}
            </div>

            {/* ═══ Asset Properties ═══ */}
            <div className={'grid grid-cols-1 lg:grid-cols-12 gap-6'}>
                {/* Left column */}
                <div className={'lg:col-span-7 flex flex-col gap-6'}>
                    <BasicInfoSection
                        data={data}
                        mode={mode}
                        serviceName={productInfo?.category?.name ?? undefined}
                        formName={formData?.name}
                        onNameChange={(name) => updateFormField('name', name)}
                    />
                    <LocationSection
                        data={data}
                        mode={mode}
                        floorValue={formData?.floor}
                        onFloorChange={updateFloor}
                    />
                    <RoomHouseSection
                        data={data}
                        mode={mode}
                        hoursLocked={showsOpeningHours && !hoursEditable}
                        hasUnsavedChanges={hasUnsavedChanges}
                        onAssigned={handleCancel}
                    />
                    {showsOpeningHours && (
                        mode === 'edit' && hoursEditable ? (
                            <OpeningHoursSection
                                openingHours={formData?.openingHours ?? []}
                                onChange={updateOpeningHours}
                                onValidityChange={setOpeningHoursValid}
                                mode={'edit'}
                                emptyMeansAlwaysOpen
                            />
                        ) : (
                            <OpeningHoursSection
                                openingHours={data.Group?.location?.openingHours ?? []}
                                mode={'view'}
                                emptyMeansAlwaysOpen
                            />
                        )
                    )}
                    {isRoomObject && (
                        <AccommodationSection
                            data={data}
                            amenities={mode === 'edit' ? formData?.accomodationdetail.amenities : data.accomodationdetail?.amenities}
                            useCase={useCases[0]}
                            mode={mode}
                            formData={formData?.accomodationdetail}
                            onAccommodationChange={updateAccommodation}
                            onAmenitiesChange={updateAmenities}
                        />
                    )}
                    {!isRoomObject && (
                        <>
                            {/* What this object is an instance of, as the
                                product page shows it (`productIdentity`, the
                                server's display rule). Description and photo
                                are the product's, shared with every sibling, so
                                they are shown for orientation and edited where
                                they live — not here. */}
                            <div className={'bg-text-contrast rounded-[5px] shadow-sm px-5 py-4 flex flex-col gap-4'}>
                                <DetailField
                                    label={t('asset_detail.fields.product')}
                                    value={productName}
                                    emptyLabel={'na'}
                                >
                                    {data.Group?.id ? (
                                        <Link
                                            to={makeProductDetailsUrl(data.Group.id)}
                                            className={'text-sm text-primary hover:underline'}
                                        >
                                            {productName ?? t('asset_detail.fields.product')}
                                        </Link>
                                    ) : undefined}
                                </DetailField>
                                <DetailField
                                    label={t('asset_detail.fields.description')}
                                    value={productDescription}
                                    emptyLabel={'na'}
                                />
                                <ProductPhotoField
                                    photo={data.productIdentity?.photo}
                                    label={t('services.products.form.image_label')}
                                />
                                <p className={'text-xs text-foreground/60'}>
                                    {t('asset_detail.product_fields_hint')}
                                </p>
                            </div>
                            <ObjectIdentitySection
                                mode={mode}
                                serialNumber={data.accomodationdetail?.serialNumber}
                                volumeCm3={data.accomodationdetail?.volumeCm3}
                                note={data.accomodationdetail?.note}
                                draft={formData?.objectAttributes}
                                onChange={updateObjectAttributes}
                            />
                        </>
                    )}
                </div>

                {/* Right column — desktop only */}
                <div className={'lg:col-span-5 hidden lg:flex flex-col gap-6'}>
                    <ImagesSection
                        featuredImage={cover}
                        additionalImages={data.additionalImages}
                        maxImages={5}
                        coverMissing={coverMissing}
                        // A room's featured image is its Group's — the object's
                        // own photo (`replaceGroupFeaturedImage` writes that
                        // Group only), editable in room scope whether or not
                        // its product is shared (#2923). Elsewhere read-only,
                        // and a station unit shows only its own photos.
                        mode={objectMode}
                        assetId={assetId}
                        pendingFeaturedImage={pendingFeaturedImage}
                        pendingAdditionalImages={pendingAdditionalImages}
                        pendingDeletions={pendingDeletions}
                        onFeaturedImageSelect={setPendingFeaturedImage}
                        onAdditionalImagesAdd={(files) => setPendingAdditionalImages((prev) => [...prev, ...files])}
                        onImageDelete={(url) => setPendingDeletions((prev) => [...prev, url])}
                        onPendingAdditionalDelete={(index) => setPendingAdditionalImages((prev) => prev.filter((_, i) => i !== index))}
                        isUploadingImages={isUploadingImages}
                        isReplacingFeaturedExternal={isReplacingFeaturedImage}
                    />
                </div>
            </div>

            {/* ═══ Service Configuration ═══ */}
            <div className={'grid grid-cols-1 lg:grid-cols-12 gap-6'}>
                <div className={'lg:col-span-7 flex flex-col gap-6'}>
                    {isRoomObject && (
                        <PoliciesSection
                            policies={mode === 'edit' ? formData?.accomodationdetail.policies : data.accomodationdetail?.policies}
                            mode={mode}
                            onPoliciesChange={updatePolicies}
                        />
                    )}
                    <AdditionalServicesSection
                        services={data.assetAdditionalServices}
                        mode={mode}
                        formServices={formData?.additionalServices}
                        onServiceChange={updateService}
                        onAddService={addService}
                        onRemoveService={removeService}
                    />
                </div>
                <div className={'lg:col-span-5 flex flex-col gap-6'}>
                    {/* Read-only per-product security deposit (Group.deposit_amount).
                        Shown for all use cases; n/a until configured. */}
                    <div className={'bg-text-contrast rounded-[5px] shadow-sm px-5 py-4'}>
                        <DetailField
                            label={t('asset_detail.fields.deposit')}
                            value={data.Group?.depositAmount != null
                                ? formatCurrency(data.Group.depositAmount, getCurrentLang())
                                : undefined}
                            emptyLabel={'na'}
                        />
                    </div>
                    {/* Member room access (#1926). Needs the group id, which
                        only exists once the object is saved — so it lives on
                        the detail page, never in the create flow. */}
                    {groupId && (
                        <RoomAccessSection
                            access={groupAccess}
                            organizations={organizations}
                        />
                    )}
                    {/* The organisations' allocation windows on this room
                        (ADR-0027): the editor the organisation's page has. */}
                    {groupId && <RoomAllocationWindows groupId={groupId} useCases={useCases} access={groupAccess} />}
                    {/* The object's own access code (master key lock PIN).
                        Baseline, not the paid add-on — ADR-0001 Amendment 3
                        moved it here from the access-management page. */}
                    {showAccessCode && assetId && (
                        <AccessCodeSection
                            assetId={assetId}
                            assetLabel={data.name}
                            mode={mode}
                        />
                    )}
                    {/* Which Product this cell offers, and — in edit mode —
                        the assignment to an existing one (pricing stream
                        slice 1). Room scope only: elsewhere the product has
                        its own page. */}
                    <ProductAssignmentSection
                        editScope={editScope}
                        productInfo={productInfo}
                        products={allProducts}
                        mode={mode}
                        value={selectedProductId}
                        onChange={setSelectedProductId}
                    />
                    {/* Prices belong to the product and are set there
                        (Services → Products), so they are shown read-only
                        here — which product this object prices from is named
                        in the section above. The long-stay discounts are the
                        OBJECT's own (`asset.pricing_discounts`) and stay
                        editable in room scope. */}
                    <PricingSection
                        priceCategoryName={data.Group?.pricecategory?.name}
                        priceCategoryId={data.Group?.pricecategory?.id}
                        priceScales={data.Group?.pricecategory?.pricescale}
                        pricingDiscounts={data.pricingDiscounts}
                        useCase={useCases[0]}
                        mode={objectMode}
                        pricesReadOnly={true}
                        discountsReadOnly={true}
                    />
                </div>
            </div>

            {/* ═══ Activity ═══ */}
            <div className={'flex flex-col gap-6 mt-2'}>
                {/* Utilization deliberately does NOT live here.
                    It is capacity over a range, keyed on the group — i.e. a
                    property of the PRODUCT, shared by every object under it.
                    Rendering it on one object's page showed "10 bookable units
                    in this group" under a heading naming a single Fach, which
                    is the product/object conflation this split exists to end
                    (PO, staging 2026-08-31). It lives on the product page. */}
                {/* #3219: dates on the clock of the object's location. */}
                <RecentBookingsSection
                    bookings={data.recentBookings}
                    mode={mode}
                    timeZone={zoneOfLocation(data.Group?.location?.timeZone)}
                />
                <OpenIssueReportsSection
                    reports={data.openIssueReports}
                    mode={mode}
                    timeZone={zoneOfLocation(data.Group?.location?.timeZone)}
                />
            </div>

            {/* Images — tablet/mobile only, at the end */}
            <div className={'lg:hidden'}>
                <ImagesSection
                    featuredImage={cover}
                    additionalImages={data.additionalImages}
                    coverMissing={coverMissing}
                    // Same scope as the desktop section above: a station
                    // unit shows only its own photos; its product's photo is
                    // the product's and shows in the Product box (PO,
                    // 2026-11-03).
                    mode={objectMode}
                    assetId={assetId}
                    pendingFeaturedImage={pendingFeaturedImage}
                    pendingAdditionalImages={pendingAdditionalImages}
                    pendingDeletions={pendingDeletions}
                    onFeaturedImageSelect={setPendingFeaturedImage}
                    onAdditionalImagesAdd={(files) => setPendingAdditionalImages((prev) => [...prev, ...files])}
                    onImageDelete={(url) => setPendingDeletions((prev) => [...prev, url])}
                    onPendingAdditionalDelete={(index) => setPendingAdditionalImages((prev) => prev.filter((_, i) => i !== index))}
                    isUploadingImages={isUploadingImages}
                    isReplacingFeaturedExternal={isReplacingFeaturedImage}
                />
            </div>

            {/* ═══ Save/Cancel Bar ═══ */}
            {mode === 'edit' && (
                <div className={'sticky bottom-0 bg-text-contrast border-t border-foreground/10 px-6 py-3 flex justify-end gap-3 shadow-lg z-10 -mx-6 -mb-6'}>
                    <CustomButton
                        title={t('common.cancel')}
                        onClick={handleCancel}
                        isFilled={false}
                        disabled={isSaving || isSavingObject || isUploadingImages || isReplacingFeaturedImage}
                    />
                    <CustomButton
                        title={t('common.save')}
                        onClick={handleSave}
                        isFilled={true}
                        disabled={
                            isSaving || isSavingObject || isUploadingImages || isReplacingFeaturedImage
                            || !formData?.name?.trim()
                            || (hoursEditable && !openingHoursValid)
                        }
                        loading={isSaving || isSavingObject || isUploadingImages || isReplacingFeaturedImage}
                    />
                </div>
            )}

            {lifecycleAction && assetId && (
                <ObjectLifecycleDialog
                    action={lifecycleAction}
                    assetId={assetId}
                    name={data.name ?? t('common.missing')}
                    isRoom={isRoomObject}
                    onClose={() => setLifecycleAction(null)}
                />
            )}

            {isDeleteOpen && assetId && (
                <HardDeleteDialog
                    name={data.name ?? t('common.missing')}
                    i18nPrefix={'asset_detail.delete'}
                    run={() => deleteAssetMutation({ assetId }).unwrap()}
                    onClose={() => setIsDeleteOpen(false)}
                    onDeleted={handleDeleted}
                />
            )}
        </div>
    );
};

export default AssetDetailPage;
